import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { MagnifyingGlass, ArrowsClockwise, Sparkle, TrendUp, TrendDown, Warning, ArrowSquareOut } from "@phosphor-icons/react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, BarChart, Bar } from "recharts";
import { fmtNum } from "./format";
import { SkeletonCard } from "./Skeleton";
import { TechnicalSeoSection } from "./TechnicalSeoSection";
import {
  WinnersLosersSection,
  OpportunityKeywordsSection,
  CannibalizationSection,
  IntentSplitSection,
  CategoryPerformanceSection,
  DeviceDeepSplitSection,
  CountryDrillSection,
  AiSearchReadinessSection,
  SearchAppearanceSection,
} from "./SearchConsoleAdvanced";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEVICE_COLORS = { MOBILE: "#4285F4", DESKTOP: "#34A853", TABLET: "#FBBC04" };
const BRAND_COLORS = { branded: "#FFEB00", non_branded: "#A78BFA" };

const KpiCard = ({ label, value, delta, invertDelta = false, testid }) => {
  const showDelta = delta != null;
  const pct = showDelta ? delta.pct : null;
  const isGood = invertDelta ? (pct != null && pct > 0) : (pct != null && pct > 0);
  // For position, invertDelta=true: lower (negative pct) is better
  const arrowUp = pct != null && pct > 0;
  const colorClass = pct == null ? "text-white/40" : (
    (invertDelta ? !arrowUp : arrowUp) ? "text-emerald-300" : "text-rose-300"
  );
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-white/45">{label}</div>
      <div className="text-lg font-semibold text-white mt-1" style={{ fontFamily: "Varela Round, sans-serif" }}>{value}</div>
      {showDelta && pct != null && (
        <div className={`text-[10px] mt-0.5 flex items-center gap-1 ${colorClass}`}>
          {arrowUp ? <TrendUp size={10} /> : <TrendDown size={10} />}
          {Math.abs(pct).toFixed(1)}% vs prev
        </div>
      )}
    </div>
  );
};

const TrendChart = ({ data }) => {
  if (!data || data.length === 0) return <div className="text-xs text-white/40 text-center py-8">No daily data in this window.</div>;
  return (
    <div className="h-56" data-testid="gsc-trend-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="gscClicksFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FFEB00" stopOpacity={0.5} />
              <stop offset="100%" stopColor="#FFEB00" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gscImprFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#4285F4" stopOpacity={0.4} />
              <stop offset="100%" stopColor="#4285F4" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#ffffff10" vertical={false} />
          <XAxis dataKey="date" tick={{ fill: "#ffffff60", fontSize: 10 }} axisLine={{ stroke: "#ffffff20" }} tickLine={false} />
          <YAxis yAxisId="left" tick={{ fill: "#ffffff60", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => fmtNum(v, { compact: true })} />
          <YAxis yAxisId="right" orientation="right" tick={{ fill: "#ffffff60", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => fmtNum(v, { compact: true })} />
          <Tooltip
            contentStyle={{ background: "#0B0B0F", border: "1px solid #ffffff20", borderRadius: 6, fontSize: 11 }}
            labelStyle={{ color: "#ffffff80" }}
            formatter={(value, name) => [fmtNum(value), name]}
          />
          <Area yAxisId="right" type="monotone" dataKey="impressions" name="Impressions" stroke="#4285F4" strokeWidth={2} fill="url(#gscImprFill)" />
          <Area yAxisId="left" type="monotone" dataKey="clicks" name="Clicks" stroke="#FFEB00" strokeWidth={2} fill="url(#gscClicksFill)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const BrandedDonut = ({ split }) => {
  if (!split) return null;
  const total = (split.branded?.clicks || 0) + (split.non_branded?.clicks || 0);
  if (total === 0) return <div className="text-xs text-white/40 text-center py-4">No click data for branded split.</div>;
  const data = [
    { name: "Branded", value: split.branded.clicks, key: "branded" },
    { name: "Non-branded", value: split.non_branded.clicks, key: "non_branded" },
  ];
  const brandedPct = (split.branded.clicks / total * 100).toFixed(1);
  return (
    <div className="flex items-center gap-4" data-testid="gsc-branded-donut">
      <div className="h-32 w-32 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" innerRadius={36} outerRadius={56} stroke="none">
              {data.map(d => <Cell key={d.key} fill={BRAND_COLORS[d.key]} />)}
            </Pie>
            <Tooltip contentStyle={{ background: "#0B0B0F", border: "1px solid #ffffff20", borderRadius: 6, fontSize: 11 }} formatter={(v) => fmtNum(v)} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-2 text-xs">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: BRAND_COLORS.branded }} />
          <span className="text-white/85 font-medium">Branded</span>
          <span className="text-white/55">{fmtNum(split.branded.clicks)} clicks · {brandedPct}%</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: BRAND_COLORS.non_branded }} />
          <span className="text-white/85 font-medium">Non-branded</span>
          <span className="text-white/55">{fmtNum(split.non_branded.clicks)} clicks · {(100 - brandedPct).toFixed(1)}%</span>
        </div>
        {Array.isArray(split.brand_terms) && split.brand_terms.length > 0 && (
          <div className="text-[10px] text-white/40 pt-1 border-t border-white/10 mt-2">
            Terms: {split.brand_terms.slice(0, 6).join(", ")}{split.brand_terms.length > 6 ? `, +${split.brand_terms.length - 6}` : ""}
          </div>
        )}
      </div>
    </div>
  );
};

const DevicePie = ({ devices }) => {
  if (!devices || devices.length === 0) return <div className="text-xs text-white/40 text-center py-4">No device data.</div>;
  const data = devices.map(d => ({ name: d.device, value: d.clicks }));
  return (
    <div className="flex items-center gap-4" data-testid="gsc-device-pie">
      <div className="h-32 w-32 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" innerRadius={36} outerRadius={56} stroke="none">
              {data.map(d => <Cell key={d.name} fill={DEVICE_COLORS[d.name] || "#71717A"} />)}
            </Pie>
            <Tooltip contentStyle={{ background: "#0B0B0F", border: "1px solid #ffffff20", borderRadius: 6, fontSize: 11 }} formatter={(v) => fmtNum(v)} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-1.5 text-xs">
        {devices.map(d => (
          <div key={d.device} className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: DEVICE_COLORS[d.device] || "#71717A" }} />
            <span className="text-white/85 font-medium capitalize">{(d.device || "").toLowerCase()}</span>
            <span className="text-white/55">{fmtNum(d.clicks)} · CTR {d.ctr}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const CountryBar = ({ countries }) => {
  if (!countries || countries.length === 0) return null;
  return (
    <div className="h-56" data-testid="gsc-country-bar">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={countries.slice(0, 10)} layout="vertical" margin={{ top: 4, right: 16, left: 32, bottom: 0 }}>
          <CartesianGrid stroke="#ffffff10" horizontal={false} />
          <XAxis type="number" tick={{ fill: "#ffffff60", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => fmtNum(v, { compact: true })} />
          <YAxis type="category" dataKey="country" tick={{ fill: "#ffffff70", fontSize: 10 }} axisLine={false} tickLine={false} width={48} />
          <Tooltip contentStyle={{ background: "#0B0B0F", border: "1px solid #ffffff20", borderRadius: 6, fontSize: 11 }} formatter={(v) => fmtNum(v)} />
          <Bar dataKey="clicks" fill="#FFEB00" radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const DeltaBadge = ({ delta, inverse = false }) => {
  if (delta == null) return <span className="text-white/30">—</span>;
  const up = delta > 0;
  const isGood = inverse ? !up : up;
  const cls = isGood ? "text-emerald-300" : "text-rose-300";
  return (
    <span className={`text-[10px] ${cls} inline-flex items-center gap-0.5`}>
      {up ? <TrendUp size={10} /> : <TrendDown size={10} />}
      {Math.abs(delta) >= 1000 ? fmtNum(Math.abs(delta), { compact: true }) : Math.abs(delta).toFixed(Math.abs(delta) < 1 ? 1 : 0)}
    </span>
  );
};

const QueriesTable = ({ rows, filter, onFilter }) => {
  if (!rows || rows.length === 0) return <div className="text-xs text-white/40 text-center py-4">No queries in this window.</div>;
  const filtered = filter === "all" ? rows : rows.filter(r => filter === "branded" ? r.branded : !r.branded);
  return (
    <div data-testid="gsc-queries-table">
      <div className="flex items-center gap-1.5 mb-2">
        {[["all", "All"], ["branded", "Branded"], ["non_branded", "Non-branded"]].map(([k, l]) => (
          <button key={k} onClick={() => onFilter(k)}
            className={`text-[10px] px-2 py-0.5 rounded ${filter === k ? "bg-[#FFEB00] text-black font-semibold" : "bg-white/[0.04] text-white/60 hover:text-white"}`}
            data-testid={`gsc-queries-filter-${k}`}>
            {l}
          </button>
        ))}
        <span className="text-[10px] text-white/40 ml-auto">{filtered.length} queries</span>
      </div>
      <div className="overflow-x-auto rounded border border-white/10">
        <table className="w-full text-xs">
          <thead className="bg-white/[0.03] text-white/55 uppercase tracking-wider text-[10px]">
            <tr>
              <th className="text-left px-3 py-2">Query</th>
              <th className="text-right px-3 py-2">Clicks</th>
              <th className="text-right px-3 py-2">Δ Clicks</th>
              <th className="text-right px-3 py-2">Impr.</th>
              <th className="text-right px-3 py-2">CTR</th>
              <th className="text-right px-3 py-2">Pos.</th>
              <th className="text-right px-3 py-2">Δ Pos.</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 25).map(r => (
              <tr key={r.query} className="border-t border-white/5 hover:bg-white/[0.02]">
                <td className="px-3 py-2 text-white/85 truncate max-w-[300px]" title={r.query}>
                  {r.query}
                  {r.branded && <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded bg-[#FFEB00]/20 text-[#FFEB00]">B</span>}
                </td>
                <td className="px-3 py-2 text-right text-white">{fmtNum(r.clicks)}</td>
                <td className="px-3 py-2 text-right"><DeltaBadge delta={r.delta_clicks} /></td>
                <td className="px-3 py-2 text-right text-white/80">{fmtNum(r.impressions)}</td>
                <td className="px-3 py-2 text-right text-white/80">{r.ctr}%</td>
                <td className="px-3 py-2 text-right text-white/80">{r.position}</td>
                <td className="px-3 py-2 text-right"><DeltaBadge delta={r.delta_position} inverse={false} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PagesTable = ({ rows }) => {
  if (!rows || rows.length === 0) return <div className="text-xs text-white/40 text-center py-4">No pages in this window.</div>;
  return (
    <div className="overflow-x-auto rounded border border-white/10" data-testid="gsc-pages-table">
      <table className="w-full text-xs">
        <thead className="bg-white/[0.03] text-white/55 uppercase tracking-wider text-[10px]">
          <tr>
            <th className="text-left px-3 py-2">Page</th>
            <th className="text-right px-3 py-2">Clicks</th>
            <th className="text-right px-3 py-2">Δ Clicks</th>
            <th className="text-right px-3 py-2">Impr.</th>
            <th className="text-right px-3 py-2">Δ Impr.</th>
            <th className="text-right px-3 py-2">CTR</th>
            <th className="text-right px-3 py-2">Pos.</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 25).map(r => (
            <tr key={r.page} className="border-t border-white/5 hover:bg-white/[0.02]">
              <td className="px-3 py-2 text-white/85 truncate max-w-[340px]" title={r.page}>
                <a href={r.page} target="_blank" rel="noreferrer" className="hover:text-[#FFEB00] inline-flex items-center gap-1">
                  {r.page.replace(/^https?:\/\/[^/]+/, "") || "/"}
                  <ArrowSquareOut size={10} className="opacity-50" />
                </a>
              </td>
              <td className="px-3 py-2 text-right text-white">{fmtNum(r.clicks)}</td>
              <td className="px-3 py-2 text-right"><DeltaBadge delta={r.delta_clicks} /></td>
              <td className="px-3 py-2 text-right text-white/80">{fmtNum(r.impressions)}</td>
              <td className="px-3 py-2 text-right"><DeltaBadge delta={r.delta_impressions} /></td>
              <td className="px-3 py-2 text-right text-white/80">{r.ctr}%</td>
              <td className="px-3 py-2 text-right text-white/80">{r.position}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SitemapHealth = ({ sitemaps }) => {
  if (!sitemaps || sitemaps.length === 0) return null;
  return (
    <div className="rounded border border-white/10 bg-white/[0.02] p-3 space-y-2" data-testid="gsc-sitemap-health">
      <div className="text-[10px] uppercase tracking-wider text-white/50">Sitemap Health</div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-white/55 uppercase tracking-wider text-[10px]">
            <tr>
              <th className="text-left px-2 py-1.5">Path</th>
              <th className="text-right px-2 py-1.5">Submitted</th>
              <th className="text-right px-2 py-1.5">Indexed</th>
              <th className="text-right px-2 py-1.5">Errors</th>
              <th className="text-right px-2 py-1.5">Warnings</th>
              <th className="text-left px-2 py-1.5">Last Submitted</th>
            </tr>
          </thead>
          <tbody>
            {sitemaps.map(sm => (
              <tr key={sm.path} className="border-t border-white/5">
                <td className="px-2 py-1.5 text-white/85 truncate max-w-[280px]" title={sm.path}>{(sm.path || "").replace(/^https?:\/\/[^/]+/, "") || sm.path}</td>
                <td className="px-2 py-1.5 text-right text-white/80">{fmtNum(sm.urls_submitted)}</td>
                <td className="px-2 py-1.5 text-right text-white/80">{fmtNum(sm.urls_indexed)}</td>
                <td className="px-2 py-1.5 text-right">
                  {sm.errors > 0 ? <span className="text-rose-300">{sm.errors}</span> : <span className="text-white/40">0</span>}
                </td>
                <td className="px-2 py-1.5 text-right">
                  {sm.warnings > 0 ? <span className="text-yellow-300">{sm.warnings}</span> : <span className="text-white/40">0</span>}
                </td>
                <td className="px-2 py-1.5 text-white/60 text-[10px]">{sm.last_submitted ? new Date(sm.last_submitted).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AiBrief = ({ ai }) => {
  if (!ai || !ai.summary) return null;
  return (
    <div className="rounded-lg border border-[#FFEB0022] bg-gradient-to-r from-[#FFEB0011] to-transparent p-4 space-y-3" data-testid="gsc-ai-brief">
      <div className="flex items-center gap-2">
        <Sparkle size={14} className="text-[#FFEB00]" />
        <span className="text-[10px] uppercase tracking-wider text-white/55">AI SEO Brief</span>
      </div>
      <p className="text-sm text-white/85 leading-relaxed">{ai.summary}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {Array.isArray(ai.improved) && ai.improved.length > 0 && (
          <div className="text-xs">
            <div className="text-emerald-300 font-medium mb-1 flex items-center gap-1"><TrendUp size={11} /> What improved</div>
            <ul className="space-y-0.5 text-white/75 list-disc list-inside">{ai.improved.slice(0, 5).map((b, i) => <li key={i}>{b}</li>)}</ul>
          </div>
        )}
        {Array.isArray(ai.declined) && ai.declined.length > 0 && (
          <div className="text-xs">
            <div className="text-rose-300 font-medium mb-1 flex items-center gap-1"><TrendDown size={11} /> What declined</div>
            <ul className="space-y-0.5 text-white/75 list-disc list-inside">{ai.declined.slice(0, 5).map((b, i) => <li key={i}>{b}</li>)}</ul>
          </div>
        )}
        {Array.isArray(ai.keywords_to_watch) && ai.keywords_to_watch.length > 0 && (
          <div className="text-xs">
            <div className="text-yellow-300 font-medium mb-1">Keywords needing attention</div>
            <ul className="space-y-0.5 text-white/75 list-disc list-inside">{ai.keywords_to_watch.slice(0, 5).map((b, i) => <li key={i}>{b}</li>)}</ul>
          </div>
        )}
        {Array.isArray(ai.pages_gaining) && ai.pages_gaining.length > 0 && (
          <div className="text-xs">
            <div className="text-emerald-300 font-medium mb-1">Pages gaining visibility</div>
            <ul className="space-y-0.5 text-white/75 list-disc list-inside">{ai.pages_gaining.slice(0, 5).map((b, i) => <li key={i}>{b}</li>)}</ul>
          </div>
        )}
        {Array.isArray(ai.pages_losing) && ai.pages_losing.length > 0 && (
          <div className="text-xs">
            <div className="text-rose-300 font-medium mb-1">Pages losing visibility</div>
            <ul className="space-y-0.5 text-white/75 list-disc list-inside">{ai.pages_losing.slice(0, 5).map((b, i) => <li key={i}>{b}</li>)}</ul>
          </div>
        )}
      </div>
      {ai.next_action && (
        <div className="border-t border-white/10 pt-3 text-xs">
          <span className="text-white/55 uppercase tracking-wider text-[10px]">Next Action · </span>
          <span className="text-white/90">{ai.next_action}</span>
        </div>
      )}
    </div>
  );
};

const SearchConsoleWidget = ({ clientId, dateRange }) => {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [queryFilter, setQueryFilter] = useState("all");

  const sd = dateRange?.start_date;
  const ed = dateRange?.end_date;

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const res = await axios.get(`${API}/clients/${clientId}/search-console/status`);
      setStatus(res.data);
    } catch {
      setStatus(null);
    } finally {
      setLoadingStatus(false);
    }
  }, [clientId]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const loadData = useCallback(async () => {
    if (!sd || !ed) return;
    if (!status?.connected || !status?.property_linked) return;
    setLoading(true); setErr("");
    try {
      const res = await axios.get(`${API}/clients/${clientId}/search-console/insights`, { params: { start_date: sd, end_date: ed } });
      setInsights(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Failed to load Search Console data");
      setInsights(null);
    } finally {
      setLoading(false);
    }
  }, [clientId, sd, ed, status?.connected, status?.property_linked]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loadingStatus) return <SkeletonCard rows={4} />;

  if (status && !status.configured) {
    return (
      <div className="rounded border border-white/10 bg-white/[0.02] p-4 text-xs text-white/60" data-testid="gsc-widget-not-configured">
        Google Search Console is not configured for this workspace. A super-admin needs to set `GSC_CLIENT_ID` + `GSC_CLIENT_SECRET` in the backend env.
      </div>
    );
  }

  if (!status?.connected) {
    return (
      <div className="rounded border border-white/10 bg-white/[0.02] p-4 flex items-center gap-3" data-testid="gsc-widget-not-connected">
        <MagnifyingGlass size={22} className="text-white/60" />
        <div>
          <div className="text-sm text-white/85 font-medium">Search Console not connected</div>
          <div className="text-[11px] text-white/50">Connect this client in the Edit Client panel to see SEO performance here.</div>
        </div>
      </div>
    );
  }

  if (!status?.property_linked) {
    return (
      <div className="rounded border border-yellow-500/30 bg-yellow-500/[0.05] p-4 flex items-center gap-3" data-testid="gsc-widget-no-property">
        <Warning size={18} className="text-yellow-300" />
        <div>
          <div className="text-sm text-white/85 font-medium">OAuth connected — but no verified property is linked yet</div>
          <div className="text-[11px] text-white/55">Go to Edit Client → Google Search Console → &quot;Fetch verified properties&quot; and pick one.</div>
        </div>
      </div>
    );
  }

  const k = insights?.kpis;
  const d = insights?.kpi_delta || {};

  return (
    <div className="space-y-4" data-testid="gsc-widget">
      {/* Header strip */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[11px] text-white/55">
          <MagnifyingGlass size={14} className="text-[#FFEB00]" />
          <span>Property: <span className="text-white/85 font-medium">{insights?.site_url || status?.site_url}</span></span>
          {insights?.range?.prev_start_date && (
            <span className="text-white/40">· vs {insights.range.prev_start_date} → {insights.range.prev_end_date}</span>
          )}
        </div>
        <button
          onClick={() => loadData()}
          disabled={loading}
          className="flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded border border-white/15 text-white/70 hover:bg-white/[0.04] disabled:opacity-50"
          data-testid="gsc-refresh"
        >
          <ArrowsClockwise size={12} /> {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {err && (
        <div className="rounded border border-red-500/30 bg-red-500/[0.05] p-3 text-xs text-red-200" data-testid="gsc-error">{err}</div>
      )}

      {loading && !insights ? (
        <SkeletonCard rows={6} />
      ) : insights && (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="gsc-kpis">
            <KpiCard label="Clicks" value={fmtNum(k.clicks, { compact: true })} delta={d.clicks} testid="gsc-kpi-clicks" />
            <KpiCard label="Impressions" value={fmtNum(k.impressions, { compact: true })} delta={d.impressions} testid="gsc-kpi-impressions" />
            <KpiCard label="CTR" value={`${k.ctr}%`} delta={d.ctr} testid="gsc-kpi-ctr" />
            <KpiCard label="Avg Position" value={k.position.toFixed(2)} delta={d.position} invertDelta testid="gsc-kpi-position" />
          </div>

          {/* Trend chart */}
          <div className="rounded border border-white/10 bg-white/[0.02] p-4">
            <div className="text-[10px] uppercase tracking-wider text-white/50 mb-2">Daily Clicks & Impressions</div>
            <TrendChart data={insights.trend} />
          </div>

          {/* AI Brief */}
          <AiBrief ai={insights.ai} />

          {/* NEW: Winners & Losers */}
          <WinnersLosersSection data={insights.winners_losers} />

          {/* NEW: Opportunity Keywords */}
          <OpportunityKeywordsSection items={insights.opportunity_keywords} />

          {/* NEW: Cannibalization */}
          <CannibalizationSection items={insights.cannibalization} />

          {/* NEW: Keyword Intent Split */}
          <IntentSplitSection intent={insights.intent_split} />

          {/* NEW: Category Performance */}
          <CategoryPerformanceSection items={insights.category_performance} />

          {/* NEW: AI Search Readiness (uses GA4) */}
          <AiSearchReadinessSection clientId={clientId} dateRange={dateRange} />

          {/* NEW: Search Appearance & AI Overviews (from GSC searchAppearance dimension) */}
          <SearchAppearanceSection items={insights.search_appearance} />

          {/* NEW (Phase 3): Technical SEO — Core Web Vitals + Indexation + Score */}
          <TechnicalSeoSection clientId={clientId} dateRange={dateRange} />

          {/* Branded + Device side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded border border-white/10 bg-white/[0.02] p-4">
              <div className="text-[10px] uppercase tracking-wider text-white/50 mb-3">Branded vs Non-branded</div>
              <BrandedDonut split={insights.branded_split} />
            </div>
            <div className="rounded border border-white/10 bg-white/[0.02] p-4">
              <div className="text-[10px] uppercase tracking-wider text-white/50 mb-3">Device Split</div>
              <DevicePie devices={insights.devices} />
            </div>
          </div>

          {/* Country */}
          {insights.countries && insights.countries.length > 0 && (
            <div className="rounded border border-white/10 bg-white/[0.02] p-4">
              <div className="text-[10px] uppercase tracking-wider text-white/50 mb-2">Top Countries by Clicks</div>
              <CountryBar countries={insights.countries} />
            </div>
          )}

          {/* NEW: Country Drill-Down */}
          <CountryDrillSection clientId={clientId} countries={insights.countries} dateRange={dateRange} />

          {/* NEW: Mobile vs Desktop Deep Split */}
          <DeviceDeepSplitSection items={insights.device_deep_split} />

          {/* Queries */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mb-2">Top Queries</div>
            <QueriesTable rows={insights.queries} filter={queryFilter} onFilter={setQueryFilter} />
          </div>

          {/* Pages */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mb-2">Top Landing Pages</div>
            <PagesTable rows={insights.pages} />
          </div>

          {/* Sitemap */}
          <SitemapHealth sitemaps={insights.sitemaps} />
        </>
      )}
    </div>
  );
};

export default SearchConsoleWidget;
